import type {
    ButtonInteraction,
    ComponentProperties,
    CreatingObject,
    CustomComponentSpec,
    DesignerEventsInteraction,
    DropInfo,
    EventDescription,
    HandleDragResult,
    HandleInteraction,
    LayoutProperties,
    PastingObjects,
    PropertyDescription,
    PropertyType,
    PropertyUpdates,
    PropertyValue,
    PropertyValueUpdates,
    RegionInteraction,
    SpacingLength,
    ToolboxItem,
    ToolboxItemComponent,
    ToolboxSection,
    UnsetPropertyValue,
    UnsetPropertyValues,
    WholeComponentInteraction,
    WholeComponentMultiInteraction,
} from "@runtime/components/Component";
import type {
    AppConfig,
    AppYaml,
    AssetYaml,
    ComponentYaml,
    DataBindingYaml,
    FormYaml,
    ModuleYaml,
    SlotDefYaml,
    SlotTargetType,
} from "@runtime/runner/data";
import type { AppIndex, ItemIndexEntry } from "./app-content";
import type { EditPath } from "./edit-state";
import type { ComponentDropTargetType } from "./edit-state";

export {
    EditPath,
    EventDescription,
    PropertyDescription,
    PropertyType,
    PropertyValue,
    CreatingObject,
    AppYaml,
    FormYaml,
    ComponentYaml,
    DataBindingYaml,
    LayoutProperties,
    ComponentProperties,
    DropInfo,
    HandleDragResult,
    SlotDefYaml,
    SlotTargetType,
    ToolboxItem,
    ToolboxSection,
    ToolboxItemComponent,
    CustomComponentSpec,
    PropertyValueUpdates,
    SpacingLength,
    UnsetPropertyValue,
    UnsetPropertyValues,
};

export const REFRESH = {
    HARD: "HARD",
    ENV: "ENV",
    DEFAULT: "DEFAULT",
} as const;

type RefreshType = keyof typeof REFRESH;

type DesignerTypes = "component" | "section" | "slot" | "template" | "slotTemplateSection" | "designInteraction";

export interface ButtonInteractionDTO extends Omit<ButtonInteraction, "callbacks"> {
    id: string;
}

export interface HandleInteractionDTO extends Omit<HandleInteraction, "callbacks"> {
    id: string;
}

export interface WholeComponentInteractionDTO extends Omit<WholeComponentInteraction, "callbacks"> {
    id: string;
}

export interface WholeComponentMultiInteractionDTO extends Omit<WholeComponentMultiInteraction, "callbacks"> {
    id: string;
}

export interface DesignerEventsInteractionDTO extends Omit<DesignerEventsInteraction, "callbacks"> {
    id: string;
}

export interface RegionInteractionDTO extends Omit<RegionInteraction, "callbacks"> {
    id: string;
    bounds: DOMRect; // For transfer, we make sure this isn't an HTMLElement
}

export type InteractionDTO =
    | ButtonInteractionDTO
    | HandleInteractionDTO
    | WholeComponentInteractionDTO
    | WholeComponentMultiInteractionDTO
    | DesignerEventsInteractionDTO
    | RegionInteractionDTO;

export interface DesignInfoDTO {
    propertyDescriptions: PropertyDescription<PropertyType>[];
    events: EventDescription[];
    interactions: InteractionDTO[];
    unsetPropertyValues: UnsetPropertyValues;
}

export interface ContainerDesignInfoDTO {
    layoutPropertyDescriptions: PropertyDescription<PropertyType>[];
    layoutPropertyValues: LayoutProperties;
    interactions: InteractionDTO[];
}

export interface HoverObjectDTO {
    type: DesignerTypes;
    name: string;
    bounds: DOMRect;
}

export interface IdentifySelectedComponentDTO {
    name: string;
}

export interface SelectedComponentDataDTO extends IdentifySelectedComponentDTO {
    name: string;
    type: string;
    bounds: DOMRect;
    propertyValues: { [prop: string]: any };
    designInfo: DesignInfoDTO;
    containerDesignInfo?: ContainerDesignInfoDTO;
}

interface SectionDataDTO {
    id: string;
    title: string;
    bounds: DOMRect;
    propertyValues: { [prop: string]: any };
    designInfo: DesignInfoDTO;
}

export interface IdentifySelectedSectionDTO {
    id: string;
    name: string;
}

export interface SelectedComponentSectionDataDTO extends SectionDataDTO, IdentifySelectedSectionDTO {
    name: string; // The component name, so we can take advantage of "all selection types have "name" properties
}

export interface IdentifySelectedSlotSectionDTO {
    id: string;
    name: string;
}

export interface SelectedSlotSectionDataDTO extends SectionDataDTO, IdentifySelectedSlotSectionDTO {
    name: string; // the slot name
}

export interface SelectedSlotDataDTO {
    name: string;
    bounds: DOMRect;
    oneComponent: boolean;
    templateComponent?: {
        propertyValues: { [prop: string]: any };
        designInfo: DesignInfoDTO;
    };
    containerDesignInfo: ContainerDesignInfoDTO;
}

export interface BoundsChangeDTO {
    type: Exclude<DesignerTypes, "template">;
    name: string;
    id?: string;
    bounds?: DOMRect;
}

export interface InteractionsChangeDTO {
    type: "component" | "slot";
    name: string;
    interactions: InteractionDTO[];
}

export type SelectedComponentDTO = { type: "component"; data: SelectedComponentDataDTO };
export type SelectedSlotDTO = { type: "slot"; data: SelectedSlotDataDTO };

export type SelectedObjectDTO =
    | SelectedComponentDTO
    | SelectedSlotDTO
    | { type: "section"; data: SelectedComponentSectionDataDTO };

export type IdentifySelectedObjectDTO =
    | { type: "component"; data: IdentifySelectedComponentDTO }
    | { type: "slot"; data: IdentifySelectedComponentDTO }
    | { type: "section"; data: IdentifySelectedSectionDTO };

export type MaybePromise<F> = F extends (...args: infer Args) => infer R ? (...args: Args) => Promise<R> | R : never;

export type RpcBackend<Backend> = {
    [fnName in keyof Backend]: MaybePromise<Backend[fnName]>;
};

export interface StdoutDTO {
    fn: "stdout";
    text: string;
    fromServer?: boolean;
}

export interface PythonExceptionDTO {
    fn: "pythonException";
    traceback: { filename: string; lineno: string }[];
    type: string;
    msg: string;
    errorObj?: any;
}

export interface RuntimeErrorDTO {
    fn: "runtimeError";
    error: string;
    msg?: string;
    url?: string | null | undefined;
    line?: number | null | undefined;
    col?: number | null | undefined;
    stackTrace?: any;
}

export interface MouseEventDTO {
    type: string;
    altKey: boolean;
    button: number;
    buttons: number;
    clientX: number;
    clientY: number;
    ctrlKey: boolean;
    metaKey: boolean;
    // movementX: number;
    // movementY: number;
    // offsetX: number;
    // offsetY: number;
    pageX: number;
    pageY: number;
    screenX: number;
    screenY: number;
    shiftKey: boolean;
    x: number;
    y: number;
}

export type ConsoleOutputDTO = PythonExceptionDTO | StdoutDTO | RuntimeErrorDTO;

// IDE RPC functions (implemented in IDE, called from designer)
export type CodeItemResponse = ModuleYaml | FormYaml | AssetYaml | undefined;

export type IdeRpcFunctions = {
    notifyEditForm: (editForm: EditFormData) => void;
    getCodeItem: (
        itemType: "module" | "form" | "asset",
        moduleName: string,
        dependencyAppId: string | null
    ) => Promise<CodeItemResponse>;
    updateComponentProperties: (componentUpdates: ComponentUpdate[]) => void;
    requestChangeEditingPath: (editPath: EditPath, formName: string) => void;
    preRequestChangeEditingPath: (editPath: EditPath, formName: string) => void;
    requestEditForm: (depId: string | undefined, formName: string) => void;
    focusDesigner: () => void;
    select: (objects: SelectedObjectDTO[]) => void;
    objectHover: (object: HoverObjectDTO | null) => void;
    createObject: (
        target: ComponentDropTargetDTO,
        creatingObject: CreatingObject,
        dropInfo: DropInfo,
        clearCreatingObject: boolean
    ) => void;
    pasteObjects: (pastingObjects: PastingObjects, target: ComponentDropTargetDTO, dropInfo: DropInfo) => void;
    notifyDropZones: (dzs: DropZoneDTO[] | null) => void;
    updateDropZones: (dzs: DropZoneUpdateDTO[]) => void;
    newHeight: (height: number) => void;
    objectBoundsChanged: (changes: BoundsChangeDTO[]) => void;
    componentInteractionsChanged: (changes: (InteractionsChangeDTO | null)[]) => void;
    componentUnsetPropertyValuesChanged: (componentName: string, unsetPropertyValues: UnsetPropertyValues) => void;
    moveObjects: (
        objects: { type: "component" | "slot"; name: string }[],
        target: ComponentDropTargetDTO,
        dropInfo: DropInfo
    ) => void;
    ready: () => void;
    consoleOutput: (output: ConsoleOutputDTO) => void;
    setDragging: (dragging: boolean) => void;
    mouseEvent: (e: MouseEventDTO) => void;
    requestSetFormProperty: (formName: string, propertyName: string, propertyValue: any) => void;
    notifyOrphanedObjects: (componentNames: string[], slotNames: string[]) => void;
    editFormResized: (bounds: DOMRect | undefined) => void;
};

// Designer RPC functions (implemented in Designer, called from IDE)

export type DesignerRpcFunctions = {
    loadApp: (appConfig: AppConfig, appIndex: AppIndex, debugOrigin: string) => void;
    updateIndex: (appIndex: AppIndex) => void;
    updateConfig: (appConfig: AppConfig) => void;
    notifyAddForm: (form: ItemIndexEntry) => void;
    editForm: (
        topLevelFormName: string,
        editPath: EditPath,
        initialSelection?: IdentifySelectedObjectDTO[]
    ) => EditFormData;
    deleteObjects: (objects: SelectedObjectDTO[]) => void;
    componentInteraction: (callbackId: string, callback: string, ...args: any[]) => any;
    renameComponent: (oldName: string, newName: string) => void;
    renameSlot: (oldName: string, newName: string) => void;
    refreshEditingForm: (type?: RefreshType) => void;
    getCustomComponents: () => { customComponents: CustomComponentSpec[]; toolboxSections: ToolboxSection[] };
    setComponentPropertyValues: (componentName: string, updates: PropertyValueUpdates) => PropertyValueUpdates;
    setSectionPropertyValues: (
        componentName: string,
        id: string,
        updates: { [prop: string]: any }
    ) => PropertyValueUpdates;
    setSlotTemplatePropertyValues: (slotName: string, updates: PropertyValueUpdates) => PropertyValueUpdates;
    setComponentLayoutProperties: (
        componentName: string,
        updates: { [prop: string]: any }
    ) => PropertyValueUpdates | undefined | null;
    setSlotLayoutProperties: (
        slotName: string,
        updates: { [prop: string]: any }
    ) => PropertyValueUpdates | undefined | null;
    setCreatingObject: (object: CreatingObject | null) => void;
    setPastingObjects: (objects: PastingObjects | null, immediate?: boolean) => void;
    select: (object: IdentifySelectedObjectDTO, append?: boolean) => void;
    hover: (object: IdentifySelectedObjectDTO | null) => void;
    hoverDropZone: (dropZoneId: DropZoneId | null) => void;
    deselect: (object: IdentifySelectedObjectDTO) => void;
    deselectAll: () => void;
    heightChanged: () => void;
    cleanupLayoutProperties: (componentName: string) => PropertyUpdates | undefined;
    getDesignInfo: (componentName: string) => DesignInfoDTO | undefined;
    getProperties: (componentNames: string[]) => { [componentName: string]: PropertyDescription[] };
    setPropagateMouseEvents: (propagate: boolean) => void;
    setHighlight: (highlight: boolean) => void;
    provideCodeItem: (type: string, codePath: string, depId: null, item: CodeItemResponse) => void;
    beginDragObjects: (
        objects: "selection" | IdentifySelectedObjectDTO[],
        fromPosition: { x: number; y: number }
    ) => void;
    completeDrag: (dropZoneId: DropZoneId) => void;
    cancelDrag: () => void;
    dropCreatingObject: (dropZoneId: DropZoneId, createAnother: boolean) => void;
    invalidateRecordSchemas: (schemaName?: string) => void;
    takeScreenShot: () => Blob | null;
    mouseEvent: (e: MouseEventDTO) => void;
    scrollIntoView(object: IdentifySelectedObjectDTO): void;
};

// There's a tiny bit of duplication here, but if we factor it out then TypeScript no longer infers helpful argument types for autocomplete.
export type IdeRpcCaller = <FnName extends keyof IdeRpcFunctions>(
    fn: FnName,
    ...args: Parameters<IdeRpcFunctions[FnName]>
) => Promise<ReturnType<IdeRpcFunctions[FnName]>>;
export type IdeRpcRaiser = <FnName extends keyof IdeRpcFunctions>(
    fn: FnName,
    ...args: Parameters<IdeRpcFunctions[FnName]>
) => void;
export type DesignerRpcCaller = <FnName extends keyof DesignerRpcFunctions>(
    fn: FnName,
    ...args: Parameters<DesignerRpcFunctions[FnName]>
) => Promise<ReturnType<DesignerRpcFunctions[FnName]>>;
export type DesignerRpcRaiser = <FnName extends keyof DesignerRpcFunctions>(
    fn: FnName,
    ...args: Parameters<DesignerRpcFunctions[FnName]>
) => void;

export interface BreadcrumbElement {
    className: string;
    editPath: EditPath;
}

export type Breadcrumb = BreadcrumbElement[];

export interface EditFormData {
    className: string;
    breadcrumb: Breadcrumb;
    bounds?: DOMRect; // Only present if this is not the top-level form.
}

export type DropZoneId = number;

export interface DropZoneDTO {
    //originalBounds: DOMRect;
    id: DropZoneId;
    target: IdentifySelectedObjectDTO & { type: "component" | "slot" };
    minChildIdx?: number;
    maxChildIdx?: number;
}

export interface DropZoneUpdateDTO {
    id: number;
    originalBounds: DOMRect;
    bounds: DOMRect;
    targetBounds: DOMRect;
    isCurrent: boolean; // Is this the dropzone you'll land in if you drop now, whether or not you're actually directly over it.
    occlusions: DOMRect[];
    name?: string;
}

export interface ComponentDropTargetDTO {
    type: ComponentDropTargetType;
    name: string;
}

// The IDE only understands bounds updates - anything else is a full reload
export type SectionUpdatesDTO = {
    [id: string]: {
        bounds?: DOMRect;
        propertyValues?: PropertyValueUpdates;
    };
};

export interface ComponentUpdateBase {
    propertyUpdates: ComponentProperties;
    layoutPropertyUpdates: LayoutProperties;
    sectionUpdates?: SectionUpdatesDTO;
}

export interface NamedComponentUpdate extends ComponentUpdateBase {
    componentName: string;
}

export interface SlotTemplateUpdate extends ComponentUpdateBase {
    slotName: string;
}

export type ComponentUpdate = NamedComponentUpdate | SlotTemplateUpdate;

export class FormDidntInstantiateError extends Error {
    constructor() {
        super(`The requested form did not instantiate`);
        this.name = "FormDidntInstantiateError";
    }
}
